/*
 * The type ladder — HIG1 (GOALS/GOALS-DESIGN.html#hig1, 2026-09-14).
 *
 * One ladder per platform, derived from Apple's Human Interface Guidelines
 * text-style tables as read on 2026-09-14 (Typography → Specifications):
 * macOS built-in text styles and the iOS/iPadOS Dynamic Type "Large"
 * (default) table. The contract declares the type roles (contract.ts,
 * THEME_TYPE_SCALE_ROLES); this module values them. Points are CSS pixels on
 * Apple displays, so the ladders are written in px.
 *
 * Density is a Desk choice, never a platform one: on macOS the compact
 * density keeps Apple's small end verbatim (caption 10, footnote 10,
 * subheadline 11) and the comfortable density lifts the small roles one step
 * so dense chrome reads at 11–12; body, headline, title and display never
 * move. iOS ignores density — Dynamic Type governs there. Nothing here goes
 * under Apple's minimum (10 on macOS, 11 on iOS).
 */
import { THEME_TYPE_SCALE_ROLES, themeFamilyCssVariable, type ThemeCssVariable } from "./contract";

export type TypeScalePlatform = "macos" | "ios";
export type TypeScaleDensity = "comfortable" | "compact";

/** The declared roles plus the two small Apple styles the Desk floor needs. */
export const TYPE_SCALE_ROLES = [...THEME_TYPE_SCALE_ROLES, "footnote", "subheadline"] as const;
export type TypeScaleRole = (typeof TYPE_SCALE_ROLES)[number];

export interface TypeStyle {
  /** Font size in px (points on Apple displays). */
  readonly size: number;
  /** Line height in px. */
  readonly leading: number;
  /** CSS font-weight. */
  readonly weight: 400 | 500 | 600 | 700;
}

export type TypeLadder = Readonly<Record<TypeScaleRole, TypeStyle>>;

const style = (size: number, leading: number, weight: TypeStyle["weight"] = 400): TypeStyle =>
  Object.freeze({ size, leading, weight });

/** Apple macOS built-in text styles (HIG Typography, Specifications, macOS). */
export const APPLE_MACOS_TEXT_STYLES = Object.freeze({
  largeTitle: style(26, 32),
  title1: style(22, 26),
  title2: style(17, 22),
  title3: style(15, 20),
  headline: style(13, 16, 700),
  body: style(13, 16),
  callout: style(12, 15),
  subheadline: style(11, 14),
  footnote: style(10, 13),
  caption1: style(10, 13),
  caption2: style(10, 13, 500),
} as const);

/** Apple iOS/iPadOS Dynamic Type styles at the Large (default) size. */
export const APPLE_IOS_TEXT_STYLES = Object.freeze({
  largeTitle: style(34, 41),
  title1: style(28, 34),
  title2: style(22, 28),
  title3: style(20, 25),
  headline: style(17, 22, 600),
  body: style(17, 22),
  callout: style(16, 21),
  subheadline: style(15, 20),
  footnote: style(13, 18),
  caption1: style(12, 16),
  caption2: style(11, 13),
} as const);

/** Apple's stated minimum text size per platform. */
export const TYPE_MINIMUM_PX: Readonly<Record<TypeScalePlatform, number>> = Object.freeze({
  macos: 10,
  ios: 11,
});

const MACOS_COMPACT: TypeLadder = Object.freeze({
  display: APPLE_MACOS_TEXT_STYLES.largeTitle,
  title: APPLE_MACOS_TEXT_STYLES.title2,
  headline: style(13, 16, 600),
  body: APPLE_MACOS_TEXT_STYLES.body,
  label: APPLE_MACOS_TEXT_STYLES.subheadline,
  subheadline: APPLE_MACOS_TEXT_STYLES.subheadline,
  footnote: APPLE_MACOS_TEXT_STYLES.footnote,
  caption: APPLE_MACOS_TEXT_STYLES.caption1,
  code: style(12, 16),
});

const MACOS_COMFORTABLE: TypeLadder = Object.freeze({
  ...MACOS_COMPACT,
  label: APPLE_MACOS_TEXT_STYLES.callout,
  subheadline: APPLE_MACOS_TEXT_STYLES.callout,
  footnote: APPLE_MACOS_TEXT_STYLES.callout,
  caption: APPLE_MACOS_TEXT_STYLES.subheadline,
});

const IOS: TypeLadder = Object.freeze({
  display: APPLE_IOS_TEXT_STYLES.largeTitle,
  title: APPLE_IOS_TEXT_STYLES.title2,
  headline: APPLE_IOS_TEXT_STYLES.headline,
  body: APPLE_IOS_TEXT_STYLES.body,
  label: APPLE_IOS_TEXT_STYLES.subheadline,
  subheadline: APPLE_IOS_TEXT_STYLES.subheadline,
  footnote: APPLE_IOS_TEXT_STYLES.footnote,
  caption: APPLE_IOS_TEXT_STYLES.caption1,
  code: style(15, 20),
});

export const TYPE_LADDERS: Readonly<Record<TypeScalePlatform, Readonly<Record<TypeScaleDensity, TypeLadder>>>> =
  Object.freeze({
    macos: Object.freeze({ comfortable: MACOS_COMFORTABLE, compact: MACOS_COMPACT }),
    ios: Object.freeze({ comfortable: IOS, compact: IOS }),
  });

export interface TypeScaleInput {
  readonly platform: TypeScalePlatform;
  readonly density?: TypeScaleDensity;
}

/** `--ncl-type-<role>` for the size; `-leading` and `-weight` beside it. */
export function typeScaleCssVariable(role: TypeScaleRole, part: "size" | "leading" | "weight" = "size"): ThemeCssVariable {
  const base = (THEME_TYPE_SCALE_ROLES as readonly string[]).includes(role)
    ? themeFamilyCssVariable("typeScale", role as (typeof THEME_TYPE_SCALE_ROLES)[number])
    : (`--ncl-type-${role}` as ThemeCssVariable);
  return part === "size" ? base : (`${base}-${part}` as ThemeCssVariable);
}

export function resolveTypeLadder(input: TypeScaleInput): TypeLadder {
  return TYPE_LADDERS[input.platform][input.density ?? "comfortable"];
}

/** Project a platform's ladder into the contract's custom properties. */
export function resolveTypeScale(input: TypeScaleInput): Readonly<Record<ThemeCssVariable, string>> {
  const ladder = resolveTypeLadder(input);
  const out: Record<string, string> = {};
  for (const role of TYPE_SCALE_ROLES) {
    const s = ladder[role];
    out[typeScaleCssVariable(role)] = `${s.size}px`;
    out[typeScaleCssVariable(role, "leading")] = `${s.leading}px`;
    out[typeScaleCssVariable(role, "weight")] = String(s.weight);
  }
  return Object.freeze(out) as Readonly<Record<ThemeCssVariable, string>>;
}

/** The ladder as a `:root` stylesheet block, for surfaces that prefer static CSS. */
export function typeScaleCss(input: TypeScaleInput, selector = ":root"): string {
  const vars = resolveTypeScale(input);
  const lines = Object.entries(vars).map(([name, value]) => `  ${name}: ${value};`);
  return `${selector} {\n${lines.join("\n")}\n}\n`;
}
